/**
 * videos.ts — the /videos library.
 *
 * The list itself lives in client/src/data/videos.json, edited from the admin
 * portal (Content → Videos) and committed to main by api/admin/videos.ts, so
 * the page stays static and prerendered. This module turns a pasted link from
 * YouTube, LinkedIn, Instagram or TikTok into what the page needs to embed it.
 * Nothing is hosted here: the videos stay on those platforms.
 */
import VIDEOS_JSON from "@/data/videos.json";

import { parseVideoUrl, type Video } from "./videoUrl";
export * from "./videoUrl";

export const VIDEOS: Video[] = VIDEOS_JSON as Video[];

/** Newest first, then the order they were added. */
export function sortedVideos(list: Video[] = VIDEOS): Video[] {
  return list.map((v, i) => ({ v, i }))
    .sort((a, b) => b.v.publishedAt.localeCompare(a.v.publishedAt) || a.i - b.i)
    .map(({ v }) => v);
}

/** Topics in use, most videos first. */
export function videoTopics(list: Video[] = VIDEOS): string[] {
  const n = new Map<string, number>();
  for (const v of list) if (v.topic) n.set(v.topic, (n.get(v.topic) ?? 0) + 1);
  return Array.from(n.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

/** The cover image a card shows: the admin's choice, else the platform's. */
export function videoThumbnail(v: Video): string | undefined {
  return v.thumbnail || parseVideoUrl(v.url)?.thumbnail;
}
